import {useState, useMemo, useCallback} from "react";
import {useLocation, useNavigate} from "react-router-dom";
import {useTranslation} from "react-i18next";
import "../styles/SelectCharacter.scss";
import {BackButton, Button, LanguageSelector} from "../components";
import {getArtistFromPath, Artist} from "../utils/artist.utils";
import {path} from "../router/path";
import {SLIDER_CONFIG} from "../assets/constants/sliderConfig";
import {CharacterInfo} from "../services/chat/assets/constants/characterInfo";
import {useCharacter} from "../services/chat";
import {getCurrentLng, SupportedLng} from "../utils/language.utils";
import {SessionTimer} from "../services/session";

const SelectCharacter = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const artist = getArtistFromPath(location.pathname);
  const {t} = useTranslation("common");

  const handleForegroundSessionNone = useCallback(() => {
    navigate(path.waiting(artist), {replace: true});
  }, [navigate, artist]);

  const handleServiceUnavailable = useCallback(() => {
    navigate(path.error(artist), {replace: true});
  }, [navigate, artist]);

  const {
    getCharactersByArtist,
    changeSelectedCharacter,
    selectedCharacter,
    saveCharacter,
    handleClearSession,
  } = useCharacter({
    artist,
    onForegroundSessionNone: handleForegroundSessionNone,
    onServiceUnavailable: handleServiceUnavailable,
  });

  const characters = getCharactersByArtist(artist);

  const handleBack = () => {
    handleClearSession();
    navigate(path.waiting(artist), {replace: true});
  };

  const handleSelect = () => {
    saveCharacter();
    navigate(path.play(artist), {replace: true});
  };

  return (
    <div className="select-character-container">
      <div className="select-character-background">
        <img
          src={selectedCharacter?.background}
          className="select-character-background-image"
          alt=""
        />
      </div>
      <header className="select-character-header">
        <BackButton artist={artist} onClick={handleBack} iconType="close" />
        <LanguageSelector artist={artist} />
      </header>
      <div className="select-character-body">
        <div className="select-character-panel">
          <div className="select-character-content">
            <div className="select-character-instruction">
              {t("selectCharacter.instruction")}
            </div>

            <div className="select-character-layout-wrapper">
              <CharacterSelector
                characters={characters}
                selectedCharacter={selectedCharacter}
                artist={artist}
                onCharacterChange={changeSelectedCharacter}
              />

              <Button
                artist={artist}
                label={t("selectCharacter.selectButton")}
                onClick={handleSelect}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SelectCharacter;

type CharacterSelectorProps = {
  characters: CharacterInfo[];
  selectedCharacter: CharacterInfo | null;
  artist: Artist;
  onCharacterChange: (character: CharacterInfo) => void;
};

const CharacterSelector = ({
  characters,
  selectedCharacter,
  artist,
  onCharacterChange,
}: CharacterSelectorProps) => {
  const currentLanguage: SupportedLng = getCurrentLng();
  const navigate = useNavigate();

  const currentIndex = useMemo(() => {
    if (!selectedCharacter) {
      return 0;
    }
    const index = characters.findIndex(
      (char) => char.id === selectedCharacter.id,
    );
    return index !== -1 ? index : 0;
  }, [selectedCharacter, characters]);

  const [dragStart, setDragStart] = useState<number>(0);
  const [dragOffset, setDragOffset] = useState<number>(0);
  const [isDragging, setIsDragging] = useState<boolean>(false);

  const handleThumbnailClick = (character: CharacterInfo) => {
    onCharacterChange(character);
  };

  const handlePrev = () => {
    if (currentIndex > 0) {
      onCharacterChange(characters[currentIndex - 1]);
    }
  };

  const handleNext = () => {
    if (currentIndex < characters.length - 1) {
      onCharacterChange(characters[currentIndex + 1]);
    }
  };

  const handleDragStart = (
    e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>,
  ) => {
    const clientX = "touches" in e ? e.touches[0].clientX : e.clientX;
    setDragStart(clientX);
    setIsDragging(true);
    setDragOffset(0);
  };

  const handleDragMove = (
    e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>,
  ) => {
    if (!isDragging) return;
    const clientX = "touches" in e ? e.touches[0].clientX : e.clientX;
    const diff = clientX - dragStart;
    setDragOffset(diff);
  };

  const handleDragEnd = () => {
    if (!isDragging) return;

    if (Math.abs(dragOffset) > SLIDER_CONFIG.DRAG_THRESHOLD) {
      if (dragOffset > 0 && currentIndex > 0) {
        onCharacterChange(characters[currentIndex - 1]);
      }
      if (dragOffset < 0 && currentIndex < characters.length - 1) {
        onCharacterChange(characters[currentIndex + 1]);
      }
    }

    setIsDragging(false);
    setDragOffset(0);
  };

  if (characters.length === 0) return null;

  const slidePercent = 100 / characters.length;
  const translateXPercent = currentIndex * slidePercent;

  const handleExpire = () => {
    navigate(path.waiting(artist), {replace: true});
  };

  return (
    <div className="select-character-carousel-wrapper">
      <div className="select-character-thumbnails">
        {characters.map((character) => (
          <button
            key={`${artist}-${character.id}-${character.identifier}`}
            type="button"
            className={`select-character-thumbnail ${artist} ${
              selectedCharacter?.id === character.id ? "active" : ""
            } ${character.identifier}`}
            onClick={() => handleThumbnailClick(character)}
            aria-label={character.information[currentLanguage].name}
            aria-pressed={selectedCharacter?.id === character.id}
          >
            <img
              src={character.thumbnail}
              alt=""
              className="select-character-thumbnail-image"
            />
          </button>
        ))}
      </div>
      <div className="select-character-card-container">
        <div
          className="select-character-card-wrap"
          onMouseDown={handleDragStart}
          onMouseMove={handleDragMove}
          onMouseUp={handleDragEnd}
          onMouseLeave={handleDragEnd}
          onTouchStart={handleDragStart}
          onTouchMove={handleDragMove}
          onTouchEnd={handleDragEnd}
          style={{
            cursor: isDragging ? "grabbing" : "grab",
            userSelect: "none",
          }}
        >
          <button
            type="button"
            className={`select-character-arrow select-character-arrow-prev ${artist}`}
            onClick={handlePrev}
            disabled={currentIndex <= 0}
            aria-label="Previous character"
          >
            <svg
              width="40"
              height="40"
              viewBox="0 0 40 40"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden
            >
              <g transform="translate(11, 8)">
                <path
                  d="M12 6 L6 12 L12 18"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </g>
            </svg>
          </button>

          <div className="select-character-card-viewport">
            <div
              className="select-character-card-slider"
              style={{
                width: `${characters.length * 100}%`,
                transform: `translateX(calc(-${translateXPercent}% + ${dragOffset}px))`,
                transition: isDragging
                  ? "none"
                  : `transform ${SLIDER_CONFIG.TRANSITION_DURATION}ms ease-in-out`,
              }}
            >
              {characters.map((character) => {
                return (
                  <div
                    key={`${artist}-${character.id}-${character.identifier}`}
                    className={`select-character-card ${character.identifier}`}
                    style={{
                      width: `${slidePercent}%`,
                      flexShrink: 0,
                    }}
                  >
                    <div className="select-character-card-greeting">
                      {character.information[currentLanguage].greeting}
                    </div>
                    <div className="select-character-card-image-wrap">
                      <img
                        src={character.image}
                        alt=""
                        className="select-character-card-image"
                        draggable={false}
                      />
                    </div>
                    <div className="select-character-card-character-info">
                      <div className="select-character-card-character-name">
                        {character.information[currentLanguage].name}
                      </div>
                      <div className="select-character-card-tags">
                        {character.information[currentLanguage].personality}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <button
            type="button"
            className={`select-character-arrow select-character-arrow-next ${artist}`}
            onClick={handleNext}
            disabled={currentIndex >= characters.length - 1}
            aria-label="Next character"
          >
            <svg
              width="40"
              height="40"
              viewBox="0 0 40 40"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g transform="translate(11, 8)">
                <path
                  d="M6 6 L12 12 L6 18"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </g>
            </svg>
          </button>
        </div>
        <div className="select-character-session-timer-wrapper">
          <SessionTimer onExpire={handleExpire} />
        </div>
      </div>
    </div>
  );
};
